import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, KeyRound, Receipt, Users } from 'lucide-react';
import { VENUE_EVENTS, rooms } from '@/lib/shared-types';
import { formatCurrency } from '@/lib/i18n';
import { GUEST_FALLBACK_CURRENCY } from '@/lib/guest';
import { loadGuestChecks, visitDue, visitTotal, type GuestCheck } from '@/lib/checks';
import { useRealtime } from '@/Hooks/useRealtime';
import { Card, CardContent } from '@/components/ui/card';
import { useAppSelector } from '@/Redux/store';

/**
 * This table's visit: how the rest of the table joins it, and what it has run up
 * between them.
 *
 * It replaces the party control centre, and it is deliberately smaller than what
 * it replaces. A party was a roster — named people, each with a spending policy
 * a host could raise or lower and an approval queue that blocked them. None of
 * that exists any more: a guest is a phone that scanned a table, the server
 * knows how many phones are on a visit only as `guestCount`, and no guest can
 * act on another guest at all. What is left is the two things a group genuinely
 * shares, and both hang off the session rather than off a roster: the PIN that
 * lets the next phone onto this same visit, and the bill the visit is running.
 */
export default function Visit() {
  const { t, i18n } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const [checks, setChecks] = useState<GuestCheck[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!session?.sessionId) return;
    loadGuestChecks(session.sessionId)
      .then(setChecks)
      .catch(() => setChecks([]))
      .finally(() => setLoading(false));
  }, [session?.sessionId]);

  useEffect(load, [load]);

  // The one room a guest token may enter besides its own orders'.
  useRealtime(
    VENUE_EVENTS.CHECK_UPDATED,
    load,
    session?.sessionId ? [rooms.guest(session.sessionId)] : [],
  );

  const currency = checks[0]?.currency ?? session?.currency ?? GUEST_FALLBACK_CURRENCY;
  const money = (value: string | number) => formatCurrency(value, currency, i18n.language);

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('guest.visit.shareTitle')}</h2>
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-start gap-3">
              <Users className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <p className="text-sm text-muted-foreground">
                {session?.tableNumber
                  ? t('guest.visit.shareHint', { number: session.tableNumber })
                  : t('guest.visit.shareHintNoTable')}
              </p>
            </div>

            {/* The PIN comes back only to the scan that OPENED the visit, and
                only where the venue gates joining. A guest who joined afterwards
                is told what to ask for rather than shown a number they do not
                have — inventing one would send the next person to a locked
                table. */}
            {session?.sessionPin ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-accent-soft p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <KeyRound className="h-3.5 w-3.5 shrink-0" />
                    <span>{t('guest.visit.pinLabel')}</span>
                  </div>
                  <div
                    dir="ltr"
                    className="text-2xl font-semibold tabular-nums tracking-[0.35em] text-accent"
                  >
                    {session.sessionPin}
                  </div>
                </div>
              </div>
            ) : (
              <p className="rounded-lg border border-line p-4 text-sm text-muted-foreground">
                {t('guest.visit.pinNone')}
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('guest.visit.billTitle')}</h2>
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-4 text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : checks.length ? (
              <>
                <dl className="space-y-1 p-4 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt>{t('guest.visit.charged')}</dt>
                    <dd className="tabular-nums">{money(visitTotal(checks))}</dd>
                  </div>
                  <div className="flex justify-between gap-3 font-semibold">
                    <dt>{t('guest.bill.due')}</dt>
                    <dd className="tabular-nums">{money(visitDue(checks))}</dd>
                  </div>
                </dl>
                <Link to="/bill" className="flex items-center gap-3 border-t border-line p-4">
                  <Receipt className="h-5 w-5 shrink-0 text-accent" />
                  <span className="flex-1 text-sm font-medium">
                    {t('guest.visit.openBills')}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:rotate-180" />
                </Link>
              </>
            ) : (
              <p className="p-4 text-sm text-muted-foreground">{t('guest.bill.empty')}</p>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
